import React, { forwardRef, useState } from 'react';
import { CustomModal } from '@components';
import {
    Box,
    FormControl,
    InputLabel,
    MenuItem,
    OutlinedInput,
    Select,
    Typography
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { apiSetLimitOrder, apiGetLimitOrder, apiGetTtfEur, apiGetTtfChf } from '@store';
import { showToast } from '@utils';

const PriceNotification = forwardRef(({ handleClose, eur, marketData, currency = 'EUR' }, ref) => {
    const dispatch = useDispatch();
    const { user } = useSelector((state) => state.user);
    const contractData = marketData ?? eur;

    const [state, setState] = useState({
        market: 'TTF',
        product: 'TTF',
        username: user?.Username,
        email: user?.Email,
        UserId: user?.UserId,
        provider: '',
        contract: '',
        type: '',
        price_gt: '',
        price_lt: '',
        purchase: 0,
        currency
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setState((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const priceGt = parseFloat(state.price_gt);
        const priceLt = parseFloat(state.price_lt);
        const hasPriceGt = state.price_gt !== '';
        const hasPriceLt = state.price_lt !== '';

        if ((hasPriceGt && (!Number.isFinite(priceGt) || priceGt <= 0)) ||
            (hasPriceLt && (!Number.isFinite(priceLt) || priceLt <= 0)) ||
            (!hasPriceGt && !hasPriceLt)) {
            showToast('error', 'Price must be greater than 0');
            return;
        }

        const payload = {
            ...state,
            price_gt: Number.isFinite(priceGt) ? priceGt : 0,
            price_lt: Number.isFinite(priceLt) ? priceLt : 0,
            currency,
        };

        const response = await dispatch(apiSetLimitOrder([payload]));
        if (response?.meta?.requestStatus === 'fulfilled') {
            showToast('success', 'Price Notification Set Successfully');
            handleClose();
            dispatch(apiGetLimitOrder());
            dispatch(currency === 'CHF' ? apiGetTtfChf() : apiGetTtfEur());
        } else if (response?.meta?.requestStatus === 'rejected') {
            showToast('error', response?.payload || 'Something went wrong');
        }
    };

    return (
        <CustomModal
            handleClose={handleClose}
            handleSubmit={handleSubmit}
            modalTitle={`Set Price for Notification`}
            buttonText={'Set Price Notification'}
            ref={ref}
            disabled={state.price_gt || state.price_lt ? false : true}
        >
            <Box>
                <Box className="flex gap-3" mb={2}>
                    <Box className="flex flex-col gap-3 w-full">
                        <FormControl fullWidth>
                            <InputLabel id="demo-simple-select-label">
                                Provider
                            </InputLabel>
                            <Select
                                labelId="demo-simple-select-label"
                                id="demo-simple-select"
                                label="Provider"
                                name="provider"
                                onChange={handleChange}
                                value={state.provider}
                                required
                            >
                                {user?.CanBuyFrom?.map((option, index) => (
                                    <MenuItem key={index} value={option}>
                                        {option}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl fullWidth>
                            <InputLabel id="demo-simple-select-label">
                                Contract
                            </InputLabel>
                            <Select
                                labelId="demo-simple-select-label"
                                id="demo-simple-select"
                                label="Contract"
                                name="contract"
                                onChange={handleChange}
                                value={state.contract}
                                required
                            >
                                {contractData?.symbols?.map((option, index) => (
                                    <MenuItem key={index} value={option}>
                                        {option}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl fullWidth>
                            <InputLabel id="demo-simple-select-label">
                                Bid/Ask
                            </InputLabel>
                            <Select
                                labelId="demo-simple-select-label"
                                id="demo-simple-select"
                                label="Month"
                                name="type"
                                onChange={handleChange}
                                value={state.type}
                                required
                            >
                                <MenuItem value="bid">Bid</MenuItem>
                                <MenuItem value="ask">Ask</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>
                    <Box className="flex flex-col gap-3 w-full">
                        <FormControl fullWidth={true} variant="outlined">
                            <InputLabel
                                id="priceGtLabel"
                                sx={{ bgcolor: '#F5F5F5', p: '0 3px' }}
                                shrink
                            >
                                Price Greater Than
                            </InputLabel>
                            <OutlinedInput
                                id="price_gt"
                                label="Price Greater Than"
                                type="number"
                                name="price_gt"
                                placeholder="Trade if price greater than..."
                                onKeyDown={(e) =>
                                    e.key === 'e' && e.preventDefault()
                                }
                                onChange={handleChange}
                                value={state.price_gt}
                                disabled={state.price_lt ? true : false}
                                inputProps={{ min: 0, step: 'any' }}
                            />
                        </FormControl>
                        <FormControl fullWidth={true} variant="outlined">
                            <InputLabel
                                id="priceLtLabel"
                                sx={{ bgcolor: '#F5F5F5', p: '0 3px' }}
                                shrink
                            >
                                Price Lower Than
                            </InputLabel>
                            <OutlinedInput
                                id="price_lt"
                                placeholder="Trade if price lower than..."
                                label="Price Lower Than"
                                type="number"
                                name="price_lt"
                                onKeyDown={(e) =>
                                    e.key === 'e' && e.preventDefault()
                                }
                                onChange={handleChange}
                                value={state.price_lt}
                                disabled={state.price_gt ? true : false}
                                inputProps={{ min: 0, step: 'any' }}
                            />
                        </FormControl>
                    </Box>
                </Box>
                <Typography variant="caption">
                    You will get notified by email when the contract you set for
                    goes higher than or less than the prices you have set.
                </Typography>
            </Box>
        </CustomModal>
    );
});

export default PriceNotification;
